import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import { Breadcrumbs, Link, Typography, Paper, Grid, Box, CircularProgress } from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { ROUTES } from '../../../../constants/routes';
import { CustomButton } from '../../../../components/CustomButton';
import { CustomLoadingButton } from '../../../../components/CustomLoadingButton';
import { CustomInput } from '../../../../components/CustomInput';
import { CustomInputWithMask } from '../../../../components/CustomInputWithMask';
import { CustomModal } from '../../../../components/CustomModal';
import { CustomSelect } from '../../../../components/CustomSelect';
import { inviteUser } from '../../../../store/user/actions';
import { getListRoles } from '../../../../store/role/actions';
import { getCompanyBranches, getDepartmentsOfBranch } from '../../../../store/company/actions';
import { REGULAR_EXPRESSIONS } from '../../../bonuses/constants';
import { visibleBySectionPermission } from '../../../../utils/visibleBySectionPermission';
import { PERMISSIONS } from '../../../../constants/variables';
import { setUsersPermissions } from '../../../../utils/setUsersPermissions';

const validationSchema = yup.object({
  lastName: yup.string().required('Обязательное поле').max(256, 'Максимум 256 символов'),
  firstName: yup.string().required('Обязательное поле').max(256, 'Максимум 256 символов'),
  middleName: yup.string().max(256, 'Максимум 256 символов'),
  email: yup.string().required('Обязательное поле').email('Неверный формат').matches(REGULAR_EXPRESSIONS.email_validation_pattern, 'Неверный формат'),
  phone: yup.string().test('len', 'Неверный формат', val => val?.length === 15 || !val),
  roleId: yup.object().shape({
    label: yup.string().required('Обязательное поле'),
    id: yup.string().required('Обязательное поле')
  }),
  branch_id: yup.object().shape({
    label: yup.string().required('Обязательное поле'),
    id: yup.string().required('Обязательное поле')
  })
});

export const InviteTeamPerson = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [isCancelModal, setIsCancelModal] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const userInviteState = useSelector((state) => state.userInvite);
  const { fetching, status, message } = userInviteState;

  const roleListState = useSelector((state) => state.roleList);
  const { items: roleOptions, fetching: fetchingRoleOptions } = roleListState;

  const currentUserState = useSelector((state) => state.currentUser);
  const { userData: currentUser } = currentUserState;

  const companyBranchesState = useSelector((state) => state.company);
  const { branches: branchesOptions, fetching: fetchingBranchesOptions, companyInfo } = companyBranchesState;

  const { userPermissions, currentUsersPermissions } = useSelector((state) => state.userPermissions);
  const permissions = setUsersPermissions(userPermissions, currentUsersPermissions?.permissions);

  const { handleSubmit, values, handleChange, errors, handleBlur, setFieldValue, isValid, dirty } =
    useFormik({
      initialValues: {
        phone: '',
        firstName: null,
        lastName: null,
        middleName: '',
        roleId: null,
        email: null,
        branch_id: null,
        company_id: null,
        department_id: null
      },
      validationSchema: validationSchema,
      validateOnChange: true,
      validateOnBlur: true,
      onSubmit: (values) => {
        const newValues = {
          phone: values.phone,
          firstName: values.firstName,
          lastName: values.lastName,
          middleName: values.middleName,
          roleId: values.roleId.id,
          email: values.email,
          branch_id: values.branch_id.id,
          company_id: currentUser.company_id || companyInfo.id,
          department_id: values.department?.id,
        };
        setIsSubmit(true);
        return dispatch(inviteUser(newValues));
      }
    });

  const currentBranch = branchesOptions?.find((branch) => branch.id === values.branch_id?.id);

  useEffect(() => {
    if (isSubmit && status === 201) {
      history.push(ROUTES.TEAM);
    } // eslint-disable-next-line
  }, [status, isSubmit]);

  useEffect(() => {
    if (values.branch_id) {
      dispatch(getDepartmentsOfBranch(values.branch_id.id));
    }
  }, [dispatch, values.branch_id]);

  useEffect(() => {
    dispatch(getListRoles());
    dispatch(getCompanyBranches());
  }, [dispatch]);

  const isPageVisible =
    visibleBySectionPermission(permissions, [PERMISSIONS.USER.ListUsers.value, PERMISSIONS.USER.CreateUsers.value,], 'or') &&
    visibleBySectionPermission(permissions, PERMISSIONS.USER.InviteUsers.value);

  if (fetching) {
    return (
      <Box
        sx={{
          marginTop: '150px',
          textAlign: 'center'
        }}>
        <CircularProgress data-testid="CircularProgress" />
      </Box>
    );
  }

  if (!isPageVisible) {
    return <></>;
  }

  return (
    <>
      <Typography mb="24px" variant="h1" sx={{ color: 'text.primary' }} component="h1" data-testid="Title">
        Пригласить
      </Typography>
      <Breadcrumbs mb="24px" aria-label="breadcrumb" data-testid="Breadcrumbs">
        <Link component={RouterLink} underline="hover" color="inherit" to={ROUTES.TEAM}>
          Команда
        </Link>
        <Typography color="text.secondary">Пригласить</Typography>
      </Breadcrumbs>
      <Paper
        sx={{
          padding: '24px'
        }}>
        <form onSubmit={handleSubmit}>
          <Box sx={{ marginBottom: '24px' }}>
            <Grid container spacing={2}>
              <Grid item>
                <CustomInput
                  name="lastName"
                  value={values.lastName}
                  placeholder={`Фамилия *`}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  variant="outlined"
                  sx={{ width: 240 }}
                  size="small"
                  error={values.lastName !== null && errors.lastName}
                  helperText={values.lastName !== null && errors.lastName}
                  disabled={fetching}
                />
              </Grid>
              <Grid item>
                <CustomInput
                  name="firstName"
                  value={values.firstName}
                  placeholder="Имя *"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  variant="outlined"
                  sx={{ width: 240 }}
                  size="small"
                  error={values.firstName !== null && errors.firstName}
                  helperText={values.firstName !== null && errors.firstName}
                  disabled={fetching}
                />
              </Grid>
              <Grid item>
                <CustomInput
                  name="middleName"
                  value={values.middleName}
                  placeholder="Отчество"
                  onChange={handleChange}
                  variant="outlined"
                  sx={{ width: 240 }}
                  size="small"
                  disabled={fetching}
                  error={values.middleName !== null && errors.middleName}
                  helperText={values.middleName !== null && errors.middleName}
                />
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ marginBottom: '24px' }}>
            <Grid container spacing={2}>
              <Grid item>
                <CustomInputWithMask
                  name="phone"
                  value={values.phone}
                  placeholder="Номер телефона"
                  onChange={handleChange}
                  variant="outlined"
                  sx={{ width: 240 }}
                  size="small"
                  error={values.phone !== null && errors.phone}
                  helperText={values.phone !== null && errors.phone}
                  disabled={fetching}
                />
              </Grid>
              <Grid item>
                <CustomInput
                  name="email"
                  value={values.email}
                  placeholder="Email *"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  variant="outlined"
                  sx={{ width: 240 }}
                  size="small"
                  error={values.email !== null && errors.email}
                  helperText={values.email !== null && errors.email}
                  disabled={fetching}
                />
              </Grid>
              <Grid item>
                <CustomSelect
                  name="roleId"
                  value={values.roleId}
                  placeholder="Роль *"
                  onChange={() => {
                    return handleChange;
                  }}
                  onBlur={handleBlur}
                  variant="outlined"
                  sx={{ width: 240 }}
                  size="small"
                  options={roleOptions}
                  loading={fetchingRoleOptions}
                  setFieldValue={setFieldValue}
                  error={values.roleId !== null && errors.roleId}
                  helperText={values.roleId !== null && errors.roleId}
                  disabled={fetching}
                />
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ marginBottom: '24px' }}>
            <Grid container spacing={2}>
              <Grid item>
                <CustomSelect
                  name="branch_id"
                  value={values.branch_id}
                  placeholder="Филиал *"
                  onChange={() => {
                    return handleChange;
                  }}
                  onBlur={handleBlur}
                  variant="outlined"
                  sx={{ width: 240 }}
                  size="small"
                  options={branchesOptions}
                  setFieldValue={setFieldValue}
                  error={values.branch_id !== null && errors.branch_id}
                  helperText={values.branch_id !== null && errors.branch_id}
                  disabled={fetchingBranchesOptions}
                />
              </Grid>
              <Grid item>
                <CustomSelect
                  name="department"
                  value={values.department}
                  placeholder="Департамент"
                  onChange={() => {
                    return handleChange;
                  }}
                  variant="outlined"
                  sx={{ width: 240 }}
                  size="small"
                  setFieldValue={setFieldValue}
                  options={currentBranch?.departments}
                  disabled={fetching}
                />
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ color: 'error.main', fontSize: '1rem' }} data-testid="ErrorMessage">{message}</Box>
            <Box>
              <CustomButton
                onClick={() => setIsCancelModal(true)}
                color="secondary"
                variant="contained"
                sx={{ marginRight: '16px' }}>
                Отмена
              </CustomButton>
              <CustomLoadingButton
                type="submit"
                variant="contained"
                loading={fetching}
                disabled={!dirty || !isValid}>
                Пригласить
              </CustomLoadingButton>
            </Box>
          </Box>
        </form>
      </Paper>
      <CustomModal
        opened={isCancelModal}
        modalClose={() => setIsCancelModal(false)}
        modalSubmit={() => setIsCancelModal(false)}
        text="Вы уверены, что хотите выйти, не сохранив данные?"
        cancelBtnName="Отмена"
        submitBtnName="Да"
        submitRoute={ROUTES.TEAM}
        data-testid="CancelModal"
      />
    </>
  );
};
